import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { NextImage, RichText, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { StArticleWrapContainer, StContainer } from './ArticlePage.style';
import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import UiHeading from '../shared/Heading';
import UiBox from '../shared/Box';
import UiText from '../shared/Typography/UiText';
import UiContainer from '../shared/Container/Container';
import Head from 'next/head';

const ArticlePage: React.FC<any> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const content = props?.fields?.content;
  const headingText = props?.fields?.headingText;
  const articlePublishDate = props?.fields?.articlePublishDate;
  const articleTitle = props?.fields?.articleTitle;
  const articleConditions = props?.fields?.articleConditions;
  const articleImage = props?.fields?.articleImage;
  const publishedDateText = props?.fields?.publishedDateText;
  const date = new Date(articlePublishDate?.value);
  const articleAuthor = props?.fields?.articleAuthor;
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;

  const formattedDate = date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const imageStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'normal',
    justifyContent: 'space-between',
    width: isMobile ? '100%' : '50%',
    '& > span': {
      ...(isMobile && { minHeight: '274px' }),
    },
  };

  const renderJSONLDSchema = () => {
    // JSON-LD structured data
    const schemaData = {
      '@context': 'https://schema.org/',
      '@type': 'Article',
      headline: articleTitle?.value,
      author: {
        '@type': 'Person',
        name: articleAuthor?.value,
      },
      datePublished: formattedDate,
      image: articleImage?.value?.src,
      publisher: {
        '@type': 'Organization',
        name: siteSettings?.name?.value,
        logo: siteSettings?.logo?.value,
      },
    };
    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>
    );
  };

  return (
    <>
      {renderJSONLDSchema()}
      <StContainer container id="article-page" className="ui-article-page">
        <UiContainer styles={{ padding: '0' }}>
          <StArticleWrapContainer
            item
            container
            direction={'column'}
            id="article-page-wrap-container"
            className="ui-article-page__wrap-container"
          >
            <Grid
              container
              alignItems="stretch"
              sx={{
                flexDirection: 'row-reverse',
                backgroundColor: theme.palette.themeColor.main,
                borderTopRightRadius: isTablet ? undefined : '16px',
                borderTopLeftRadius: isTablet ? undefined : '16px',
              }}
              id="article-page-stack"
              className="ui-article-page__stack"
            >
              <Grid item xs={12} md={7} lg={6}>
                <UiBox
                  styles={{
                    padding: isMobile ? '25px' : '25px 30px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <UiBox styles={{ flex: 1 }}>
                    <Typography sx={{ color: theme.palette.white.main }}>
                      {articleConditions?.displayName}
                    </Typography>
                    <UiHeading
                      size="h1"
                      color="white"
                      isChildren
                      styles={{ fontSize: '48px', lineHeight: '100%' }}
                    >
                      {articleTitle.value}
                    </UiHeading>
                  </UiBox>
                  <Placeholder name="jss-share" rendering={props.rendering} />
                </UiBox>
              </Grid>
              <Grid item xs={12} md={5} lg={6} sx={imageStyle}>
                <NextImage
                  field={articleImage}
                  priority={true}
                  style={{
                    zIndex: 0,
                    borderTopLeftRadius: isTablet ? undefined : '16px',
                    width: '100%', // Make it responsive
                    height: isMobile ? '274px' : '100%', // Set dynamic height based on screen size
                    objectFit: 'cover', // Ensures the image is not distorted
                  }}
                />
              </Grid>
            </Grid>
            <Grid
              container
              justifyContent="center"
              className="content"
              id="article-page-content"
              sx={{ padding: '2rem 0', backgroundColor: theme.palette.white.main }}
            >
              <Grid xs={11}>
                <UiBox styles={{ marginBottom: '2rem' }}>
                  <UiHeading size="h2" color="primary">
                    {headingText?.value}
                  </UiHeading>
                </UiBox>
                <Grid
                  item
                  data-testid="content"
                  sx={{ color: theme.palette.neutral9.main, fontSize: '1.125rem', fontWeight: 400 }}
                >
                  <UiText>
                    <RichText field={content} />
                  </UiText>
                </Grid>
                <UiBox styles={{ paddingTop: '1rem' }}>
                  <UiText size="body" color="black">
                    <i>
                      {publishedDateText?.value || 'Published date:'} {formattedDate}
                    </i>
                  </UiText>
                </UiBox>
              </Grid>
            </Grid>
          </StArticleWrapContainer>
        </UiContainer>
      </StContainer>
    </>
  );
};

export default ArticlePage;
